<template>
    <section class="search-bar-with-list container">
        <!-- The label is visually hidden once there is text, not removed:
             v-show took the input's only name away. -->
        <div class="form-field search" role="search">
            <label :for="searchInputId" :class="{ 'sr-only': searchTerm !== '' }">Search neighborhoods</label>
            <SvgsSearch />
            <input :id="searchInputId" v-model="searchTerm" type="search" />
        </div>

        <!-- Results from search term. -->
        <ul v-if="searchResults.length && searchTerm.length >= 2" class="list">
            <li v-for="(item, index) in searchResults" :key="`result-${item.id}-id`" class="neighborhood">
                <CardsNeighborhoodSquare :item="item" />
            </li>
        </ul>

        <!-- No results even though user has typed in search term. Deliberately
             NOT inside .list: that becomes a four-column grid above $tablet, so
             the heading was being laid out as one grid cell and wrapping inside a
             quarter-width column. It is also not a valid child of a <ul>. -->
        <div v-else-if="!searchResults.length && searchTerm.length >= 2" class="no-results">
            <h2 class="headline-1">No neighborhoods match your search.</h2>
        </div>

        <!-- Original list. -->
        <ul v-else class="list">
            <li v-for="(item, index) in items" :key="item.id" class="neighborhood">
                <CardsNeighborhoodSquare :item="item" />
            </li>
        </ul>
    </section>
</template>

<script setup>
    const props = defineProps({
        items: {
            type: Array,
            required: true,
            default: [],
        },
    });

    const searchTerm = ref('');
    const searchResults = ref([]);
    const searchInputId = useId();

    watch(searchTerm, (currentValue) => {
        searchResults.value = [];

        if (currentValue.length < 2) return;

        // `props.items`, not a bare `items`: <script setup> exposes props to the
        // TEMPLATE only, so the bare identifier threw ReferenceError on every
        // keystroke. The throw aborted the flush before the component could
        // re-render, which is why the list never changed and no result — and no
        // "no results" message — ever appeared.
        //
        // `fields.Name` is the neighborhood title; `fields.Site` does not exist on
        // this collection, so the old comparison was reading undefined.
        const term = currentValue.toLowerCase();
        searchResults.value = props.items.filter((item) =>
            (item.fields?.Name || '').toLowerCase().includes(term),
        );
    });
</script>

<style lang="scss">
    .search-bar-with-list {

        .form-field.search {
            margin-bottom: 20px;
        }

        // `.container` ships with its padding commented out, so the message has
        // no gutter of its own to inherit and sat flush against the viewport edge.
        .no-results {
            padding: 24px 16px 48px;
            text-align: center;

            .headline-1 {
                max-width: 720px;
                margin: 0 auto;
            }
        }

        .list {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-bottom: 20px;

            @media (min-width: $tablet) {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                grid-gap: 20px;
            }

            .neighborhood {
                flex: 0 0 calc(50% - 8px);
                margin-bottom: 20px;

                figure {
                    position: relative;
                    margin-bottom: 10px;
                    padding-top: 100%;

                    img {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    .red-tag,
                    .site-tour {
                        position: absolute;
                        bottom: 15px;
                    }

                    .red-tag {
                        left: 15px;
                        padding: 10px 8px 6px;
                        background-color: $red;
                        color: $white;
                        font-size: 12px;
                        font-weight: 800;
                        text-align: center;
                    }

                    .site-tour {
                        right: 15px;
                    }
                }
            }
        }

        @media (min-width: $tablet) {
            .list {
                .neighborhood {
                    flex: 0 0 calc(33% - 20px);
                    margin-bottom: 40px;
                }

                .headline-2 {
                    font-size: 25px;
                    line-height: 25px;
                }
            }

            .form-field input {
                width: 100%;
            }

            .form-field.search {
                width: 100%;
            }
        }

        @media (min-width: $desktop) {
            .form-field.search {
                width: 100%;
            }

            .list {
                .neighborhood {
                    flex: 0 0 calc(25% - 20px);
                    margin-bottom: 40px;
                }
            }
        }
    }
</style>
